import { useCallback } from 'react';
import { useRefreshToken } from '../useRefreshToken';

/**
 * Hook centralizado para gerenciamento de autenticação
 * Centraliza a lógica de token que está duplicada em vários componentes
 * 
 * Integra com useRefreshToken existente para renovação automática
 * 
 * Uso:
 * const { getToken, refreshToken, validateToken } = useAuth();
 */
export function useAuth() {
  const { sendRefreshToken } = useRefreshToken();

  /**
   * Obtém o token atual do localStorage
   * Substitui as implementações duplicadas de getCurrentToken()
   */
  const getToken = useCallback((): string | null => {
    try {
      return localStorage.getItem("barte-auth-token");
    } catch {
      return null;
    }
  }, []);

  /**
   * Verifica se o token existe e está válido
   * Útil para validações rápidas antes de fazer requisições
   */
  const validateToken = useCallback((): boolean => {
    const token = getToken();
    return !!token && token.trim() !== '';
  }, [getToken]);

  /**
   * Obtém informações do usuário do localStorage
   * Útil para acessar dados como idSeller, email, etc.
   */
  const getUserInfo = useCallback(() => {
    try {
      const userInfo = localStorage.getItem("barte-user-info");
      return userInfo ? JSON.parse(userInfo) : null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Delega para o hook useRefreshToken existente
   * Mantém compatibilidade com implementação atual
   */
  const refreshToken = useCallback(async () => {
    return sendRefreshToken();
  }, [sendRefreshToken]);

  return {
    getToken,
    validateToken,
    getUserInfo,
    refreshToken,
  };
}